import clsx from 'clsx'
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import type { OptionItem } from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text'

import useContentContext from 'app/KinerjaKSPage/hooks/useContentContext'
import useSKPKSTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSTrackEvent'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'

import type { ForwardedRef } from 'react'
import type { SKPTabHandler } from 'types/kinerja'
import type { KinerjaKs, UpdateSKPKSPayloadWithoutNpsn } from 'types/kinerja-ks'

const errorStateClass = 'perilaku-kerja-has-error'
const errorStateMessage = 'Wajib diisi'

interface Tab3Props {
  skp: KinerjaKs
  isLoading?: boolean
  onClickNext?: (data: UpdateSKPKSPayloadWithoutNpsn) => void
  onClickSave?: (data: UpdateSKPKSPayloadWithoutNpsn) => void
}

const Tab3 = (
  {
    skp,
    isLoading = false,
    onClickNext = () => {},
    onClickSave = () => {},
  }: Tab3Props,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { contentPerilakuKerja } = useContentContext()
  const { trackClickDraftPlanning, trackClickToRangkuman } =
    useSKPKSTrackEvent()

  const selectedPerilakuKerja = Array.from({
    length: contentPerilakuKerja?.aspek?.length,
  }).map((_, index) => ({
    aspekId: skp?.perilakuKerja?.at(index)?.aspekId,
    indikatorId: skp?.perilakuKerja.at(index)?.indikatorId,
    ekspektasiId: skp?.perilakuKerja.at(index)?.ekspektasiId,
  }))

  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const defaultValues = useMemo(
    () => ({
      indikatorPerilaku:
        selectedPerilakuKerja.map((perilakuKerja, index) => {
          const indikator = contentPerilakuKerja?.aspek[index]?.indikator

          const selectedIndikator = indikator?.find(
            (indikator) => indikator.id === perilakuKerja.indikatorId
          )

          const result: OptionItem = {
            value: perilakuKerja.indikatorId,
            name: selectedIndikator?.value,
          }

          return result
        }) ?? [],
      perwujudanPerilaku:
        selectedPerilakuKerja.map((perilakuKerja, index) => {
          const indikator = contentPerilakuKerja?.aspek[index]?.indikator

          const ekspektasi = indikator?.find(
            (indikator) => indikator.id === perilakuKerja.indikatorId
          )?.ekspektasi

          const selectedEkspektasi = ekspektasi?.find(
            (ekspektasi) => ekspektasi.id === perilakuKerja.ekspektasiId
          )

          const result: OptionItem = {
            value: perilakuKerja.ekspektasiId,
            name: selectedEkspektasi?.value,
          }

          return result
        }) ?? [],
    }),
    [selectedPerilakuKerja, contentPerilakuKerja]
  )

  const { setValue, getValues, watch, clearErrors, setError, formState } =
    useForm<{
      indikatorPerilaku: OptionItem[]
      perwujudanPerilaku: OptionItem[]
    }>({
      defaultValues,
    })

  const { perwujudanPerilaku } = watch()

  const validateValues = () => {
    clearErrors()

    perwujudanPerilaku.forEach((value, i) => {
      if (!value?.name)
        setError(`perwujudanPerilaku.${i}`, {
          message: errorStateMessage,
        })
    })

    const elementsWithError = document.getElementsByClassName(errorStateClass)
    if (elementsWithError.length) {
      // Scroll to first element that has errors
      const element = elementsWithError[0]

      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const isDisabledNextButton =
    !perwujudanPerilaku?.length ||
    perwujudanPerilaku?.some(
      (perwujudan) => !perwujudan?.value || isNaN(perwujudan.value)
    )

  // Field `npsn` can be safely ignored since it will be automatically added to the payload.
  const generatePayload = useCallback((): UpdateSKPKSPayloadWithoutNpsn => {
    const perilakuKerja = contentPerilakuKerja?.aspek.map((a, i) => ({
      aspekId: a.id,
      indikatorId: getValues('indikatorPerilaku')[i]?.value,
      ekspektasiId: getValues('perwujudanPerilaku')[i]?.value,
    }))

    return {
      perilakuKerja,
    }
  }, [contentPerilakuKerja, getValues])

  const onSubmit = () => {
    trackClickToRangkuman({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PERILAKU_KERJA_PAGE',
    })

    onClickNext(generatePayload())
  }

  const onSaveDraft = useCallback(() => {
    trackClickDraftPlanning({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PERILAKU_KERJA_PAGE',
    })

    onClickSave(generatePayload())
  }, [onClickSave, generatePayload, trackClickDraftPlanning, skp])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  return (
    <>
      <div className="p-9">
        <Text variant="heading-md" as="h3">
          Pilih 1 Indikator Perilaku dan 1 Ekspektasi Khusus Pimpinan
        </Text>
        <Text className="mt-4">
          Untuk Ekspektasi Khusus Pimpinan, pilih yang Anda ingin usulkan ke Tim
          Kinerja atau Pejabat Penilai Kinerja.
        </Text>

        <a
          href={`${baseUrl}/panduan/perilaku-kerja`}
          className="underline text-blue-70 font-bold inline-block mt-2"
          target="_blank"
          rel="noopener noreferrer"
        >
          Cek Daftar Perilaku Kerja
        </a>

        <div className="mt-10 flex flex-col gap-6">
          {contentPerilakuKerja?.aspek.map(
            (perilakuKerja, perilakuKerjaIndex) => (
              <div
                key={`perilaku-kerja-${perilakuKerjaIndex}`}
                data-testid={`perilaku-kerja-${perilakuKerjaIndex}`}
                className={clsx('p-6 border border-gray-30 rounded', {
                  '!border-[#D93640]':
                    !!formState.errors.perwujudanPerilaku?.[perilakuKerjaIndex],
                  [errorStateClass]:
                    !!formState.errors.perwujudanPerilaku?.[perilakuKerjaIndex],
                })}
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-2.5 items-start">
                    <div className="flex items-center justify-center rounded-full bg-blue-70 p-3">
                      <div className="absolute flex">
                        <Text color="inverse" className="!font-bold">
                          {perilakuKerjaIndex + 1}
                        </Text>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Text variant="body-sm" color="subdued">
                        Aspek
                      </Text>
                      <Text variant="display-sm">{perilakuKerja.value}</Text>
                    </div>
                  </div>

                  <Text color="critical">
                    {
                      formState.errors.perwujudanPerilaku?.[perilakuKerjaIndex]
                        ?.message
                    }
                  </Text>
                </div>

                <div className="mt-6">
                  <Text variant="body-sm" color="subdued" className="mb-1">
                    Indikator Perilaku
                  </Text>
                  <CustomSelect
                    value={getValues(`indikatorPerilaku.${perilakuKerjaIndex}`)}
                    placeholder="Pilih"
                    isInvalid={
                      !!formState.errors.perwujudanPerilaku?.[
                        perilakuKerjaIndex
                      ]?.message
                    }
                    options={perilakuKerja.indikator.map((indikator) => ({
                      value: indikator.id,
                      name: indikator.value,
                    }))}
                    onChange={(selectedValue: OptionItem) => {
                      setValue(
                        `indikatorPerilaku.${perilakuKerjaIndex}`,
                        selectedValue
                      )

                      // Reset Perwujudan Perilaku currently selected
                      setValue(
                        `perwujudanPerilaku.${perilakuKerjaIndex}`,
                        undefined
                      )
                    }}
                    data-testid="select-indikator"
                  />
                </div>

                <div className="mt-4">
                  <Text variant="body-sm" color="subdued" className="mb-1">
                    Ekspektasi Khusus Pimpinan
                  </Text>
                  <CustomSelect
                    value={getValues(
                      `perwujudanPerilaku.${perilakuKerjaIndex}`
                    )}
                    placeholder="Pilih"
                    isInvalid={
                      !!formState.errors.perwujudanPerilaku?.[
                        perilakuKerjaIndex
                      ]?.message
                    }
                    options={
                      perilakuKerja.indikator
                        .find(
                          (indikator) =>
                            indikator.id ===
                            getValues(`indikatorPerilaku.${perilakuKerjaIndex}`)
                              ?.value
                        )
                        ?.ekspektasi?.map((ekspektasi) => ({
                          value: ekspektasi.id,
                          name: ekspektasi.value,
                        })) ?? []
                    }
                    onChange={(selectedValue: OptionItem) => {
                      setValue(
                        `perwujudanPerilaku.${perilakuKerjaIndex}`,
                        selectedValue
                      )
                    }}
                    isDisabled={
                      !watch(`indikatorPerilaku.${perilakuKerjaIndex}.value`)
                    }
                    data-testid="select-perwujudan-perilaku"
                  />
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <Separator />

      <div className="px-8 py-4 flex gap-5 justify-end">
        <Button
          size="lg"
          color="white"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={onSaveDraft}
        >
          Simpan draf
        </Button>
        <Button
          size="lg"
          color="black"
          isLoading={isLoading}
          disabled={isDisabledNextButton || isLoading}
          onClick={onSubmit}
          onClickCapture={validateValues}
        >
          Ke Rangkuman
        </Button>
      </div>
    </>
  )
}

export default forwardRef<SKPTabHandler, Tab3Props>(Tab3)
